import axios, { AxiosError, AxiosInstance } from "axios";
import * as Sentry from "@sentry/react";
import { getDirection } from "../../_stream/i18n/Streami18n";
import { setupUnauthorizedRefreshInterceptor } from "../modules/auth/core/tokenRefresh";

const baseURL = import.meta.env.VITE_APP_API_URL;

const reportResponseError = (error: AxiosError) => {
  if (!error.response) {
    return;
  }

  const { status, data, config } = error.response;
  if (status === 401) {
    return;
  }

  console.error("Response Error:", error.response);
  if (status < 500) {
    return;
  }

  const sentryError = new Error(
    `API Error [${status}] at ${config?.url}: ${
      (data as { message?: string } | undefined)?.message || "No message"
    }`
  );
  /*eslint-disable*/
  (sentryError as any).originalError = error;
  /*eslint-enable*/
  Sentry.captureException(sentryError);
};

const reportNetworkError = (
  error: AxiosError,
  extra?: Record<string, unknown>
) => {
  if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
    return;
  }
  console.error("Network Error:", error);
  Sentry.captureException(
    new Error("Network Error: " + (error.message || String(error))),
    extra ? { extra } : undefined
  );
};

export const cleanAxio = axios.create({
  baseURL: baseURL,
  withCredentials: true,
});
cleanAxio.interceptors.request.use(
  function (config) {
    const direction = getDirection();
    config.headers["language"] = direction === "rtl" ? "ar" : "en";
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

const consumerAxio = axios.create({
  baseURL: baseURL,
  withCredentials: true,
});
consumerAxio.defaults.withCredentials = true;

consumerAxio.interceptors.request.use(
  function (config) {
    const direction = getDirection();
    config.headers["language"] = direction === "rtl" ? "ar" : "en";
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axios.defaults.withCredentials = true;

const setupResponseErrorReporting = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (!error.response) {
        console.error("Request config:", error.config);
        console.error("Base URL:", baseURL);
        console.error("Full URL:", error.config?.url);

        const isNavigating =
          window.location.pathname !==
          (error.config as { _navigatingFrom?: string } | undefined)
            ?._navigatingFrom;

        reportNetworkError(error, {
          baseURL,
          requestURL: error.config?.url,
          requestMethod: error.config?.method,
          isNavigating,
        });
      } else {
        reportResponseError(error);
      }
      return Promise.reject(error);
    }
  );
};

axios.interceptors.request.use(
  function (config) {
    const direction = getDirection();
    config.headers["language"] = direction === "rtl" ? "ar" : "en";
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

setupResponseErrorReporting(axios);
setupResponseErrorReporting(consumerAxio);
setupUnauthorizedRefreshInterceptor(axios);
setupUnauthorizedRefreshInterceptor(consumerAxio);

export { consumerAxio };

export default axios;
